import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { Music, Image, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';
import '../styles/upload-page.css';

const inputClassName =
  'w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white placeholder-gray-500 text-sm';

const getFieldClassName = (hasError) =>
  `${inputClassName}${hasError ? ' border-red-500 focus:border-red-500 focus:ring-red-500/50' : ''}`;

const getDropzoneClassName = (isDragActive, hasError) => {
  if (isDragActive) return 'border-gold-50 bg-gold-50/10';
  if (hasError) return 'border-red-500/60 hover:border-red-400';
  return 'border-white/20 hover:border-gold-50/60';
};

const labelClassName = 'block text-sm font-medium text-gray-300 mb-2';

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const WESTERN_KEYS = NOTES.flatMap((note) => [
  `${note} major`,
  `${note} minor`,
]);

const parseSplitValue = (value) => {
  const num = parseFloat(String(value).replace('%', '').trim());
  return Number.isNaN(num) ? NaN : num;
};

const validateWriterSplit = (value, required = false) => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return required ? 'Split is required' : true;
  }

  const num = parseSplitValue(value);
  if (Number.isNaN(num) || num < 0 || num > 100) {
    return 'Split must be a number between 0 and 100';
  }

  return true;
};

const validateDuration = (value) => {
  if (!value || String(value).trim() === '') {
    return 'Duration is required';
  }

  const match = String(value).trim().match(/^(\d+):([0-5]\d)$/);
  if (!match) {
    return 'Duration must be in M:SS format (e.g. 3:45)';
  }

  return true;
};

const validateBpm = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return 'BPM is required';
  }

  const num = Number(value);
  if (!Number.isInteger(num) || num <= 0) {
    return 'BPM must be a whole number';
  }

  return true;
};

const SectionHeading = ({ children }) => (
  <h2 className="text-lg font-semibold text-white border-b border-white/10 pb-2">{children}</h2>
);

const FormField = ({ label, error, children, className = '' }) => (
  <div className={className}>
    <label className={`${labelClassName}${error ? ' text-red-400' : ''}`}>{label}</label>
    {children}
    {error && <p className="text-sm text-red-400 mt-1">{error.message}</p>}
  </div>
);

const WriterBlock = ({ index, register, errors, getValues, trigger }) => {
  const prefix = `writer_${index}`;
  const isPrimaryWriter = index === 1;
  const nameLabel = isPrimaryWriter ? `Writer ${index} Name *` : `Writer ${index} Name`;
  const splitLabel = isPrimaryWriter ? `Writer ${index} Split *` : `Writer ${index} Split`;

  const nameRules = isPrimaryWriter ? { required: 'Writer name is required' } : {};
  const nameField = register(prefix, nameRules);
  const splitField = register(`${prefix}_split`, {
    required: isPrimaryWriter ? 'Writer split is required' : false,
    validate: (value) => {
      if (isPrimaryWriter) {
        return validateWriterSplit(value, true);
      }

      const writerName = getValues(prefix);
      if (writerName && String(writerName).trim()) {
        return validateWriterSplit(value, true);
      }

      return true;
    },
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-lg border upload-writer-block">
      <FormField label={nameLabel} error={errors[prefix]}>
        <input
          type="text"
          {...nameField}
          onBlur={(event) => {
            nameField.onBlur(event);
            if (String(getValues(prefix) || '').trim()) {
              trigger(`${prefix}_split`);
            }
          }}
          className={getFieldClassName(Boolean(errors[prefix]))}
          placeholder="Writer name"
        />
      </FormField>
      <FormField label={splitLabel} error={errors[`${prefix}_split`]}>
        <input
          type="text"
          {...splitField}
          className={getFieldClassName(Boolean(errors[`${prefix}_split`]))}
          placeholder="e.g. 50"
        />
      </FormField>
      <FormField label={`Writer ${index} PRO`}>
        <input
          type="text"
          {...register(`${prefix}_pro`)}
          className={inputClassName}
          placeholder="e.g. ASCAP, BMI"
        />
      </FormField>
    </div>
  );
};

const SingleUpload = ({ embedded = false, onClose }) => {
  const [audioFile, setAudioFile] = useState(null);
  const [audioTouched, setAudioTouched] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageTouched, setImageTouched] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [artists, setArtists] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    getValues,
    trigger,
  } = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      track_type: 'tracks',
      lyrics: false,
      lyrics_type: '',
      composition_type: '',
      song_key: '',
    },
  });

  const containsLyrics = watch('lyrics');

  const onAudioDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setAudioFile(acceptedFiles[0]);
      setAudioError(null);
    }
  };

  const onImageDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setImageFile(acceptedFiles[0]);
      setImageError(null);
    }
  };

  const {
    getRootProps: getAudioRootProps,
    getInputProps: getAudioInputProps,
    isDragActive: isAudioDragActive,
  } = useDropzone({
    onDrop: onAudioDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.flac'],
    },
    multiple: false,
  });

  const {
    getRootProps: getImageRootProps,
    getInputProps: getImageInputProps,
    isDragActive: isImageDragActive,
  } = useDropzone({
    onDrop: onImageDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif'],
    },
    multiple: false,
  });

  const audioRootProps = getAudioRootProps();
  const imageRootProps = getImageRootProps();

  const handleAudioBlur = (event) => {
    audioRootProps.onBlur?.(event);
    setAudioTouched(true);
    if (!audioFile) {
      setAudioError('Audio file is required');
    }
  };

  const handleImageBlur = (event) => {
    imageRootProps.onBlur?.(event);
    setImageTouched(true);
    if (!imageFile) {
      setImageError('Track image is required');
    }
  };

  const showAudioError = audioTouched && audioError;
  const showImageError = imageTouched && imageError;

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        setLoadingArtists(true);
        const response = await api.get('/api/artists');
        setArtists(response.data.artists);
      } catch (error) {
        console.error('Failed to fetch artists:', error);
      } finally {
        setLoadingArtists(false);
      }
    };

    fetchArtists();
  }, []);

  const onSubmit = async (data) => {
    if (!audioFile) {
      setAudioTouched(true);
      setAudioError('Audio file is required');
      return;
    }

    if (!imageFile) {
      setImageTouched(true);
      setImageError('Track image is required');
      return;
    }

    setUploading(true);
    setUploadStatus(null);
    setImageError(null);

    try {
      const formData = new FormData();
      formData.append('audio_file', audioFile);
      formData.append('image_file', imageFile);

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'lyrics') {
          formData.append(key, value ? 'true' : 'false');
          return;
        }
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value);
        }
      });

      const response = await api.post('/api/submissions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStatus({
        type: 'success',
        message: 'Track uploaded successfully!',
        submissionId: response.data.submission_id,
      });

      reset({
        track_type: 'tracks',
        lyrics: false,
        lyrics_type: '',
        composition_type: '',
        song_key: '',
      });
      setAudioFile(null);
      setAudioTouched(false);
      setAudioError(null);
      setImageFile(null);
      setImageTouched(false);
      setImageError(null);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        type: 'error',
        message: error.response?.data?.error || 'Upload failed',
      });
    } finally {
      setUploading(false);
    }
  };

  const validateFileFields = () => {
    if (!audioFile) {
      setAudioTouched(true);
      setAudioError('Audio file is required');
    }
    if (!imageFile) {
      setImageTouched(true);
      setImageError('Track image is required');
    }
  };

  const formContent = (
    <>
        {uploadStatus && (
          <div
            className={`mb-6 p-4 rounded-md ${
              uploadStatus.type === 'success'
                ? 'bg-green-900/40 border border-green-700'
                : 'bg-red-900/40 border border-red-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              {uploadStatus.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-400" />
              )}
              <p
                className={`text-sm ${
                  uploadStatus.type === 'success' ? 'text-green-200' : 'text-red-200'
                }`}
              >
                {uploadStatus.message}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit, validateFileFields)} className="space-y-8 max-w-none">
          {/* Audio file */}
          <section className="space-y-4">
            <SectionHeading>Audio File</SectionHeading>
            <FormField label="Audio File *" error={showAudioError ? { message: audioError } : null}>
              <div
                {...audioRootProps}
                onBlur={handleAudioBlur}
                tabIndex={0}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors outline-none ${getDropzoneClassName(isAudioDragActive, showAudioError)}`}
              >
                <input {...getAudioInputProps()} />
                <Music className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                {audioFile ? (
                  <div>
                    <p className="text-sm font-medium text-white">{audioFile.name}</p>
                    <p className="text-xs text-gray-400">
                      {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-400">
                      {isAudioDragActive
                        ? 'Drop the audio file here'
                        : 'Drag & drop an audio file here, or click to select'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Supports MP3, WAV, M4A, FLAC (max 100MB)
                    </p>
                  </div>
                )}
              </div>
            </FormField>
          </section>

          {/* Track information */}
          <section className="space-y-4">
            <SectionHeading>Track Information</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField label="Track Title *" error={errors.title}>
                <input
                  type="text"
                  {...register('title', { required: 'Title is required' })}
                  className={getFieldClassName(Boolean(errors.title))}
                  placeholder="Enter track title"
                />
              </FormField>

              <FormField label="ISRC *" error={errors.isrc}>
                <input
                  type="text"
                  {...register('isrc', { required: 'ISRC is required' })}
                  className={getFieldClassName(Boolean(errors.isrc))}
                  placeholder="International Standard Recording Code"
                />
              </FormField>

              <FormField label="Release Package">
                <input
                  type="text"
                  {...register('release_package')}
                  className={inputClassName}
                  placeholder="e.g. Holiday 2024"
                />
              </FormField>

              <FormField label="Track Type">
                <select {...register('track_type')} className={inputClassName}>
                  <option value="tracks">Tracks</option>
                  <option value="stems">Stems</option>
                  <option value="instrumental">Instrumental</option>
                  <option value="acapella">Acapella</option>
                </select>
              </FormField>

              <FormField label="Artist *" error={errors.artist_id}>
                <select
                  {...register('artist_id', { required: 'Artist is required' })}
                  className={getFieldClassName(Boolean(errors.artist_id))}
                  disabled={loadingArtists}
                >
                  <option value="">Select an artist</option>
                  {artists.map((artist) => (
                    <option key={artist.id} value={artist.id}>
                      {artist.artist_name}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Featured Artist">
                <select
                  {...register('featured_artist_id')}
                  className={inputClassName}
                  disabled={loadingArtists}
                >
                  <option value="">Select a featured artist</option>
                  {artists.map((artist) => (
                    <option key={artist.id} value={artist.id}>
                      {artist.artist_name}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          </section>

          {/* Writers */}
          <section className="space-y-4">
            <SectionHeading>Songwriters</SectionHeading>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((index) => (
                <WriterBlock
                  key={index}
                  index={index}
                  register={register}
                  errors={errors}
                  getValues={getValues}
                  trigger={trigger}
                />
              ))}
            </div>
          </section>

          {/* Composition & lyrics */}
          <section className="space-y-4">
            <SectionHeading>Composition & Lyrics</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  id="lyrics"
                  {...register('lyrics')}
                  className="h-4 w-4 rounded border-white/20 bg-black/50 text-gold-50 focus:ring-gold-50"
                />
                <label htmlFor="lyrics" className="text-sm text-gray-300">
                  Contains lyrics
                </label>
              </div>

              <FormField
                label={containsLyrics ? 'Lyrics Type *' : 'Lyrics Type'}
                error={errors.lyrics_type}
              >
                <select
                  {...register('lyrics_type', {
                    validate: (value) => {
                      if (containsLyrics && !value) {
                        return 'Lyrics type is required when track contains lyrics';
                      }
                      return true;
                    },
                  })}
                  className={getFieldClassName(Boolean(errors.lyrics_type))}
                  disabled={!containsLyrics}
                >
                  <option value="">Select lyrics type</option>
                  <option value="Original">Original</option>
                  <option value="Public Domain">Public Domain</option>
                </select>
              </FormField>

              <FormField label="Composition Type *" error={errors.composition_type} className="md:col-span-2">
                <select
                  {...register('composition_type', { required: 'Composition type is required' })}
                  className={getFieldClassName(Boolean(errors.composition_type))}
                >
                  <option value="">Select composition type</option>
                  <option value="Original">Original</option>
                  <option value="Public Domain">Public Domain</option>
                </select>
              </FormField>
            </div>
          </section>

          {/* Musical metadata */}
          <section className="space-y-4">
            <SectionHeading>Musical Details</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Genre *" error={errors.genre}>
                <input
                  type="text"
                  {...register('genre', { required: 'Genre is required' })}
                  className={getFieldClassName(Boolean(errors.genre))}
                  placeholder="e.g. Holiday, Pop, R&B"
                />
              </FormField>

              <FormField label="Sub Genre">
                <input
                  type="text"
                  {...register('sub_genre')}
                  className={inputClassName}
                  placeholder="e.g. Soul, Funk"
                />
              </FormField>

              <FormField label="Key *" error={errors.song_key}>
                <select
                  {...register('song_key', { required: 'Key is required' })}
                  className={getFieldClassName(Boolean(errors.song_key))}
                >
                  <option value="">Select a key</option>
                  {WESTERN_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="BPM *" error={errors.bpm}>
                <input
                  type="number"
                  step="1"
                  {...register('bpm', { validate: validateBpm })}
                  className={getFieldClassName(Boolean(errors.bpm))}
                  placeholder="120"
                  min="1"
                />
              </FormField>

              <FormField label="Duration *" error={errors.duration}>
                <input
                  type="text"
                  {...register('duration', { validate: validateDuration })}
                  className={getFieldClassName(Boolean(errors.duration))}
                  placeholder="3:45"
                />
              </FormField>
            </div>
          </section>

          {/* Moods */}
          <section className="space-y-4">
            <SectionHeading>Moods</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((index) => (
                <FormField key={index} label={`Mood ${index}`}>
                  <input
                    type="text"
                    {...register(`mood_${index}`)}
                    className={inputClassName}
                    placeholder="e.g. Energetic, Chill"
                  />
                </FormField>
              ))}
            </div>
          </section>

          {/* Description & tags */}
          <section className="space-y-4">
            <SectionHeading>Description & Tags</SectionHeading>
            <FormField label="Song Description">
              <textarea
                {...register('song_description')}
                rows={4}
                className={inputClassName}
                placeholder="Describe your track..."
              />
            </FormField>

            <FormField label="Tags">
              <input
                type="text"
                {...register('tags')}
                className={inputClassName}
                placeholder="Christmas, Funky, Classic (comma-separated)"
              />
            </FormField>
          </section>

          {/* Production credits */}
          <section className="space-y-4">
            <SectionHeading>Production Credits</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Producer">
                <input
                  type="text"
                  {...register('producer')}
                  className={inputClassName}
                  placeholder="Producer name"
                />
              </FormField>

              <FormField label="Engineer">
                <input
                  type="text"
                  {...register('engineer')}
                  className={inputClassName}
                  placeholder="Engineer name"
                />
              </FormField>

              <FormField label="Instrument Detail Credits" className="md:col-span-2">
                <textarea
                  {...register('instrument_detail_credits')}
                  rows={3}
                  className={inputClassName}
                  placeholder="Drums, Percussion, Keys - Name; Bass - Name"
                />
              </FormField>

              <FormField label="Instruments" className="md:col-span-2">
                <input
                  type="text"
                  {...register('instruments')}
                  className={inputClassName}
                  placeholder="Drums, Bass, Guitars, Vocals, Keys"
                />
              </FormField>
            </div>
          </section>

          {/* Track image */}
          <section className="space-y-4">
            <SectionHeading>Track Image</SectionHeading>
            <FormField label="Track Image *" error={showImageError ? { message: imageError } : null}>
              <div
                {...imageRootProps}
                onBlur={handleImageBlur}
                tabIndex={0}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors outline-none ${getDropzoneClassName(isImageDragActive, showImageError)}`}
              >
                <input {...getImageInputProps()} />
                <Image className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                {imageFile ? (
                  <div>
                    <p className="text-sm font-medium text-white">{imageFile.name}</p>
                    <p className="text-xs text-gray-400">
                      {(imageFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-400">
                      {isImageDragActive
                        ? 'Drop the image here'
                        : 'Drag & drop a track image here, or click to select'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Supports JPG, PNG, WEBP, GIF (max 100MB)
                    </p>
                  </div>
                )}
              </div>
            </FormField>
          </section>

          <button
            type="submit"
            disabled={uploading}
            className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gold-50 hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? 'Uploading...' : 'Upload Track'}
          </button>
        </form>
    </>
  );

  if (embedded) {
    return (
      <div className="p-6">
        {onClose && (
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/15">
            <div className="text-xs font-bold uppercase tracking-wider text-white">Single Track</div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors text-xs uppercase tracking-wider"
            >
              Close
            </button>
          </div>
        )}
        {formContent}
      </div>
    );
  }

  return (
    <div className="upload-page">
      <div className="upload-page-header">
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold text-white">Single Track Upload</h1>
        </div>
      </div>

      <div className="upload-page-scroll">
        {formContent}
      </div>
    </div>
  );
};

export default SingleUpload;
